import React, { useCallback, useMemo, useRef } from "react";
import { SearchOption, TypeaheadInput, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { filterText } from "#components/styles_vanilla.css";
import { useGetArenasLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { SelectedTag } from "./SelectedTag";
import { ArenaEdgeType, ClientSearchProps } from "./types";

/**
 * Standalone Client Search Component
 *
 * A reusable typeahead input for selecting clients (arenas). Handles data fetching via GraphQL.
 *
 * @param props - ClientSearchProps
 * @returns A typeahead input component for client selection
 *
 * @example
 * // Standalone usage
 * <ClientSearch
 *   value="client-123"
 *   onSelect={(clientId) => setSelectedClient(clientId)}
 *   placeholder="Select a client"
 * />
 *
 * @example
 * // With custom search handler
 * <ClientSearch
 *   onSelect={handleClientSelect}
 *   onSearchClear={() => setSelectedClient(null)}
 * />
 */
export const ClientSearch = (props: ClientSearchProps) => {
  const {
    value: _value,
    values = [],
    multiselect = false,
    onSelect,
    onMultiSelect,
    onSearchClear,
    placeholder = "Search for a client",
    className,
    disabled = false,
    isClearable = true,
    isFullWidth = true,
    testId = "client-filter",
  } = props;

  const [fetchArenasQuery, { data, loading }] = useGetArenasLazyQuery();

  // Cache to store client names by ID to avoid losing labels when search results change
  const clientNamesCache = useRef<Record<string, string>>({});

  const handleSelection = useCallback(
    (selectedOption?: SearchOption) => {
      if (!selectedOption?.id) return;

      const selectedId = selectedOption.id.toString();

      // Cache the client name immediately when selected
      if (selectedOption.label) {
        clientNamesCache.current[selectedId] = selectedOption.label;
      }

      if (multiselect) {
        if (!onMultiSelect) {
          return;
        }

        // Check if already selected
        if (values.includes(selectedId)) {
          return; // Don't add duplicates
        }

        const newValues = [...values, selectedId];
        onMultiSelect(newValues);
      } else {
        onSelect(selectedId);
      }
    },
    [onSelect, onMultiSelect, multiselect, values],
  );

  const arenaList = useMemo(() => {
    const list =
      data?.arenas?.edges.map((arena: ArenaEdgeType) => ({
        id: arena.node.id,
        label: arena.node.name,
      })) || [];

    // Update cache with new arena names
    list.forEach((arena) => {
      clientNamesCache.current[arena.id] = arena.label;
    });

    return list;
  }, [data]);

  // Get selected arena names for multiselect tags
  const selectedArenas = useMemo(() => {
    if (!multiselect || !values.length) return [];

    return values.map((id) => {
      // First try to find in current search results, then fall back to cache
      const arena = arenaList.find((arena) => arena.id === id);
      const cachedName = clientNamesCache.current[id];

      return {
        id,
        label: arena?.label || cachedName || `Client ${id}`,
      };
    });
  }, [multiselect, values, arenaList]);

  const handleRemoveTag = useCallback(
    (idToRemove: string) => {
      if (!onMultiSelect) return;

      const newValues = values.filter((id) => id !== idToRemove);
      onMultiSelect(newValues);
    },
    [values, onMultiSelect],
  );

  const handleDebouncedSearch = useCallback(
    async (searchValue?: string) => {
      if (searchValue?.length) {
        await fetchArenasQuery({
          context: clientContext(),
          variables: { searchTerm: searchValue },
          fetchPolicy: "no-cache",
        });
      } else {
        if (multiselect) {
          // In multiselect mode, don't clear selections when search is cleared
          return;
        }
        onSearchClear?.();
      }
    },
    [fetchArenasQuery, onSearchClear, multiselect],
  );

  if (multiselect) {
    return (
      <Flex flexDirection="column" gap={Sizes.SM}>
        {selectedArenas.length > 0 && (
          <Flex flexWrap="wrap" gap={Sizes.XS}>
            {selectedArenas.map((arena) => (
              <SelectedTag
                key={arena.id}
                label={arena.label}
                onRemove={() => handleRemoveTag(arena.id)}
                testId={`${testId}-tag-${arena.id}`}
              />
            ))}
          </Flex>
        )}
        <TypeaheadInput
          className={className || filterText}
          data-testid={testId}
          isClearable={false} // Don't show clear button in multiselect mode
          isFullWidth={isFullWidth}
          disabled={disabled}
          isLoading={loading}
          options={arenaList}
          onSelectOption={handleSelection}
          onDebouncedSearch={handleDebouncedSearch}
          placeholder={placeholder}
        />
      </Flex>
    );
  }

  return (
    <TypeaheadInput
      className={className || filterText}
      data-testid={testId}
      isClearable={isClearable}
      isFullWidth={isFullWidth}
      disabled={disabled}
      isLoading={loading}
      options={arenaList}
      onSelectOption={handleSelection}
      onDebouncedSearch={handleDebouncedSearch}
      placeholder={placeholder}
    />
  );
};
